/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
  Grid,
  Button,
  TextField,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

import {
  addGuest,
  addReservation,
  selectGuest,
  selectReservation,
  selectIsPending,
  setReservationData,
  selectCompanyCode,
  getGuestByReservation,
  getReservationByCode,
  getReservationsByCompanyCode,
} from '../../store/widgetSlice';

import {
  setMessage,
} from '../../store/authSlice';

import {
  isRequired,
  noSpecialCharacters,
  validEmail,
  noSequentialNumbers,
  validateField,
  validateForm,
} from '../../helpers/validation';

import ForwardedTextField from '../common/ForwardedTextField';

const useStyles = makeStyles((theme) => ({
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
}));

export default function AddGuestForm() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const currentGuest = useSelector(selectGuest);
  const currentReservation = useSelector(selectReservation);
  const currentCompanyCode = useSelector(selectCompanyCode);
  const isPending = useSelector(selectIsPending);
  const [options, setOptions] = useState([]);
  const [reservationCodeValue, setReservationCodeValue] = useState('');
  const [reservationCodeInputValue, setReservationCodeInputValue] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [formValues, setFormValues] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    cardNumber: '',
    street: 'N/A',
    city: 'N/A',
    state: 'N/A',
    postalCode: 'N/A',
    country: 'US',
    reservationCode: '',
    companyCode: '',
    startDate: null,
    endDate: null,
  });

  const formRules = {
    firstName: [isRequired, noSpecialCharacters, noSequentialNumbers(8)],
    lastName: [isRequired, noSpecialCharacters, noSequentialNumbers(8)],
    email: [isRequired, validEmail],
    phoneNumber: [isRequired],
    cardNumber: [],
    street: [],
    city: [],
    state: [],
    postalCode: [],
    country: [],
    reservationCode: [isRequired],
    companyCode: [isRequired],
    startDate: [],
    endDate: [],
  };

  const handleSelect = (event, selectedOption) => {
    setReservationCodeValue((selectedOption && selectedOption.value) || selectedOption);
    setFormValues({ ...formValues, reservationCode: (selectedOption && selectedOption.value) || selectedOption });
  };

  const handleInputChange = (event, selectedOption) => {
    setReservationCodeInputValue((selectedOption && selectedOption.value) || selectedOption);
    setFormValues({ ...formValues, reservationCode: (selectedOption && selectedOption.value) || selectedOption });
  };

  const handlePhoneNumberChange = (value) => {
    setFormValues({ ...formValues, phoneNumber: value });
  };

  const handleChange = (event) => {
    setFormValues({ ...formValues, [event.target.name]: event.target.value });
  };

  const handleBlur = (event) => {
    validateField(formRules, formErrors, setFormErrors, event.target.name, event.target.value);
  };

  const handleRetrieve = (event, showSuccessMessage = true) => {
    if (event) {
      event.preventDefault();
    }

    const { companyCode, reservationCode } = formValues;

    if (formValues.reservationCode && formValues.companyCode) {
      Promise.all([
        dispatch(getGuestByReservation({ companyCode, reservationCode })).unwrap(),
        dispatch(getReservationByCode({ companyCode, reservationCode })).unwrap(),
      ])
        .then(() => {
          if (showSuccessMessage) {
            dispatch(setMessage({ message: 'Guest information retrieved', severity: 'success' }));
          }

          dispatch(setReservationData({ companyCode, reservationCode }));
        })
        .catch(() => {
          dispatch(setMessage({ message: 'Guest information could not be retrieved', severity: 'error' }));
        });
    } else {
      dispatch(setMessage({ message: 'Please provide a reservation code and a company code', severity: 'info' }));
    }
  };

  useEffect(() => {
    setFormValues((_formValues) => ({
      ..._formValues,
      ...currentGuest,
      ...currentReservation,
      ...{ companyCode: currentCompanyCode },
    }));
  },
  [currentGuest, currentReservation, currentCompanyCode]);

  useEffect(() => {
    dispatch(getReservationsByCompanyCode({ companyCode: currentCompanyCode })).unwrap()
      .then((reservations) => {
        setOptions(reservations.map((r) => ({ label: r.reservationCode, value: r.reservationCode })));
      })
      .catch(() => {
        setOptions([]);
      });
  },
  [dispatch, currentCompanyCode]);

  const handleSubmit = (event) => {
    if (event) {
      event.preventDefault();
    }

    const {
      companyCode, reservationCode,
    } = formValues;

    // Check if guest exists by reservation
    dispatch(getGuestByReservation({ companyCode, reservationCode })).unwrap()
      .then((guest) => {
        // If guest exists, retrieve matching reservation
        dispatch(getReservationByCode({ companyCode, reservationCode })).unwrap()
          .then(() => {
            handleRetrieve(event, true);
          })
          .catch(() => {
            // Create reservation if it does not exist
            dispatch(addReservation({
              guestId: guest.id,
              reservationCode: formValues.reservationCode,
              companyCode: formValues.companyCode,
              startDate: formValues.startDate,
              endDate: formValues.endDate,
            }))
              .then(() => {
                handleRetrieve(event, false);
                dispatch(setMessage({ message: 'Reservation created', severity: 'success' }));
              })
              .catch(() => {
                dispatch(setMessage({ message: 'Reservation could not be created', severity: 'error' }));
              });
          });
      })
      .catch(() => {
        // If guest is not found, add to the database
        if (validateForm(formRules, formValues, formErrors, setFormErrors)) {
          dispatch(addGuest({
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            email: formValues.email,
            phoneNumber: formValues.phoneNumber,
            cardNumber: formValues.cardNumber,
            street: formValues.street,
            city: formValues.city,
            state: formValues.state,
            postalCode: formValues.postalCode,
            country: formValues.country,
          }))
            .unwrap()
            .then((guest) => {
              // After guest is created, add a reservation tied to it
              dispatch(addReservation({
                guestId: guest.id,
                reservationCode: formValues.reservationCode,
                companyCode: formValues.companyCode,
                startDate: formValues.startDate,
                endDate: formValues.endDate,
              }))
                .then(() => {
                  handleRetrieve(event, true);
                  dispatch(setMessage({ message: 'Guest and reservation created', severity: 'success' }));
                })
                .catch(() => {
                  dispatch(setMessage({ message: 'Reservation could not be created', severity: 'error' }));
                });
            })
            .catch(() => {
              dispatch(setMessage({ message: 'Guest could not be created', severity: 'error' }));
            });
        }
      });
  };

  return (
    <form className={classes.form}>
      <h2>Retrieve Reservation</h2>
      <Grid container spacing={3}>
        <Grid align="right" item md={7} xs={12}>
          <TextField
            variant="outlined"
            margin="normal"
            id="guest-company-code"
            label="Company Code"
            name="companyCode"
            value={formValues.companyCode}
            error={!!formErrors.companyCode}
            helperText={formErrors.companyCode}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isPending || !!currentCompanyCode}
            fullWidth
            required
          />
        </Grid>
        <Grid align="left" item md={5} xs={12}>
          <Autocomplete
            freeSolo
            fullWidth
            value={reservationCodeValue}
            inputValue={reservationCodeInputValue}
            onChange={handleSelect}
            onInputChange={handleInputChange}
            onBlur={handleBlur}
            getOptionLabel={(option) => option.label || option}
            getOptionSelected={(option, selectedValue) => option.value === selectedValue}
            options={options || []}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                margin="normal"
                id="guest-reservation-code"
                label="Reservation Code"
                name="reservationCode"
                error={!!formErrors.reservationCode}
                helperText={formErrors.reservationCode}
                disabled={isPending}
                fullWidth
                required
              />
            )}
          />
        </Grid>
      </Grid>
      <Button
        type="submit"
        variant="contained"
        color="primary"
        className={classes.submit}
        onClick={handleRetrieve}
        disabled={isPending || !formValues?.companyCode || !formValues?.reservationCode}
        fullWidth
      >
        Retrieve
      </Button>
      <h2>Add Guest</h2>
      <Grid container spacing={3}>
        <Grid align="left" item md={6} xs={12}>
          <TextField
            variant="outlined"
            margin="normal"
            id="guest-firstanme"
            label="First Name"
            name="firstName"
            autoComplete="given-name"
            value={formValues.firstName}
            error={!!formErrors.firstName}
            helperText={formErrors.firstName}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isPending}
            fullWidth
            required
          />
        </Grid>
        <Grid align="right" item md={6} xs={12}>
          <TextField
            variant="outlined"
            margin="normal"
            id="guest-lastname"
            label="Last Name"
            name="lastName"
            autoComplete="family-name"
            value={formValues.lastName}
            error={!!formErrors.lastName}
            helperText={formErrors.lastName}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isPending}
            fullWidth
            required
          />
        </Grid>
      </Grid>
      <TextField
        variant="outlined"
        margin="normal"
        id="guest-email"
        label="Email"
        name="email"
        autoComplete="email"
        value={formValues.email}
        onChange={handleChange}
        onBlur={handleBlur}
        error={!!formErrors.email}
        helperText={formErrors.email}
        disabled={isPending}
        fullWidth
        required
      />
      <PhoneInput
        defaultCountry="US"
        variant="outlined"
        margin="normal"
        id="guest-phone"
        label="Phone Number"
        name="phoneNumber"
        autoComplete="tel"
        onChange={handlePhoneNumberChange}
        onBlur={handleBlur}
        disabled={isPending}
        value={formValues.phoneNumber}
        error={!!formErrors.phoneNumber}
        helperText={formErrors.phoneNumber}
        fullWidth
        required
        inputComponent={ForwardedTextField}
      />
      <Button
        type="submit"
        variant="contained"
        color="primary"
        className={classes.submit}
        onClick={handleSubmit}
        disabled={isPending}
        fullWidth
      >
        Submit
      </Button>
    </form>
  );
}
